import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { logIn, logInWithGoogle } from "services/auth/api";

import { useHandleTextFieldChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { LOGIN_VALIDATION_SCHEMA } from "./validation";
import { LoginFormProps } from "./types";
import { Routes } from "ui/modules/public/navigation";
import {
  AuthActionTypes,
  initialLoginForm,
  LoginFormValues,
  setAccessToken,
} from "services/auth";
import { useDispatch } from "react-redux";
import { PasswordField } from "ui/components/TextField/PasswordField";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { CheckBox } from "ui/components/CheckBox";
import { Button } from "ui/components/Button/Button";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { LoginIntro } from "../LoginIntro";
import GoogleSignInButton from "../GoogleSignInButton/GoogleSignInButton";

const LoginForm: React.FC<LoginFormProps> = (props) => {
  const { afterLogin } = props;
  const [formValues, setFormValues] =
    useState<LoginFormValues>(initialLoginForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const dispatch = useDispatch();

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmitted(true);

    const isValid = validateYup(formValues, LOGIN_VALIDATION_SCHEMA, setErrors);

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      completeLogin(await logIn(formValues));
    } catch (e) {
      // Ignore error
    }

    setIsLoading(false);
  };

  /** Zajednički završetak prijave — isti je bez obzira ide li kroz lozinku ili Google račun. */
  const completeLogin = (resp: { accessToken: string; refreshToken: string }) => {
    // Save token to redux
    dispatch({
      type: AuthActionTypes.SIGN_IN_SUCCESS,
      payload: {
        accessToken: resp.accessToken,
        refresToken: resp.refreshToken,
      },
    });
    // Set token to local or session storage
    setAccessToken(resp.accessToken, resp.refreshToken, formValues.keepLoggedIn);

    afterLogin();
  };

  const handleGoogleCredential = useCallback(async (credential: string) => {
    setIsLoading(true);

    try {
      completeLogin(await logInWithGoogle(credential));
    } catch (e) {
      // Ignore error
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.keepLoggedIn]);

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, keepLoggedIn: !old.keepLoggedIn }));
  }, []);

  return (
    <>
      <div className="flex h-screen w-screen">
        <LoginIntro />
        <div className="relative w-full lg:w-1/2 flex justify-center items-center h-full">
          <div className="w-[400px]">
            <div className="text-center mb-4 text-[#344054] mb-3">
              <h1 className="text-2xl mb-1">Prijava</h1>
              <p className="text-base">Dobro došli natrag!</p>
            </div>
            <form noValidate>
              <div className="mb-4">
                <label className="block text-base mb-2 font-medium leading-6 text-[#344054]">
                  Email
                </label>
                {/* Bez debounce-a: kad preglednik sam ispuni polje, korisnik ga nikada ne
                    fokusira pa nema ni blura koji bi odgođenu vrijednost proslijedio dalje —
                    prvi klik na „Prijava" bi tada pao na validaciju maila. */}
                <TextField
                  type="email"
                  name="email"
                  placeholder="Unesite email"
                  onChange={handleTextFieldChange}
                  error={isSubmitted ? errors.email : undefined}
                  value={formValues.email}
                  width="full"
                  height="big"
                  disableDebounce
                  required
                />
              </div>
              <div className="mb-2">
                <label className="block text-base mb-2 font-medium leading-6 text-[#344054]">
                  Lozinka
                </label>
                <PasswordField
                  placeholder="Unesite lozinku"
                  name="password"
                  value={formValues.password}
                  onChange={handleTextFieldChange}
                  height="big"
                  required
                  error={isSubmitted ? errors.password : undefined}
                />
              </div>
              <div className="flex items-center justify-between mb-6">
                <CheckBox
                  name="keepLoggedIn"
                  checked={formValues.keepLoggedIn}
                  onChange={handleCheckBoxChange}
                  label="Zapamti lozinku"
                />
                <div className="text-sm">
                  <Link
                    to={Routes.ForgotPasswordPage}
                    className="font-medium text-sm text-blue-500 hover:text-blue-400"
                  >
                    Zaboravili ste lozinku?
                  </Link>
                </div>
              </div>
              <div className="mb-4">
                <Button
                  onClick={handleFormSubmit}
                  variant="primary"
                  size="full"
                  className="font-medium py-4 !text-base rounded-md">
                  Prijava
                </Button>
              </div>
            </form>

            {/* Prijava vanjskim računom; korisnik mora već postojati u sustavu. */}
            <GoogleSignInButton onCredential={handleGoogleCredential} />

            {/* <ButtonLink
              variant="secondary"
              to={Routes.RegistrationPage}
              cssClassName="flex justify-center font-medium py-4 !text-base rounded-md"
              size="full"
            >
              <span >Registracija</span>
            </ButtonLink> */}
          </div>
          <Loader isLoading={isLoading ?? false}> </Loader>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
